import { HEADER_CSRF } from "@charter/shared";
import { API_URL } from "@/config/env";

/**
 * A light per-session summary of what playing felt like (docs/health.md): how long the world took
 * to load, frame rate and long frames, reconnects, the network as this client saw it (round trip,
 * gaps between snapshots), how often the server corrected its position, and panels opened then
 * closed without doing anything in them. Counts and percentiles only (no input, no positions, no
 * text). Sent every few minutes and when the page goes away, to POST /v1/client-telemetry; the
 * daily health digest and the admin Health view read it.
 */

const SEND_EVERY_MS = 10 * 60_000;
/** The first summary goes this long after the world shows, so even a short session reports. */
const EARLY_SEND_MS = 30_000;
/** A frame this long is a visible hitch. */
const LONG_FRAME_MS = 100;
const MAX_PANELS = 30;
/** Snapshots are sent every 100 ms: a gap this long is a late one. */
const LATE_SNAPSHOT_MS = 250;
/** Samples kept per window (a 10 minute window: one round trip each 5 s, 10 snapshots a second). */
const MAX_RTTS = 240;
const MAX_GAPS = 6000;
const MAX_CORRECTIONS = 200;
/**
 * Sends that are not a player doing something in a panel: movement, aiming, closing a chest as its
 * panel closes, auto-attack. They must not make a panel look used.
 */
const PASSIVE = new Set(["move", "target", "closechest", "autoattack", "stopcast", "ping"]);

interface PanelCount {
  opens: number;
  /** Closed without an intent sent while it was open. */
  empty: number;
}

const state = {
  started: performance.now(),
  loadMs: null as number | null,
  frames: 0,
  longFrames: 0,
  /** Frames per second, one sample a second (the last 600). */
  fps: [] as number[],
  secondStart: 0,
  secondFrames: 0,
  reconnects: 0,
  intents: 0,
  /** Visible time the frames cover (ms): the window a long-frame rate is per. */
  visibleMs: 0,
  /** Round trips (ms) of this window's pings. */
  rtts: [] as number[],
  /** Gaps between visible snapshots (ms), and how many were late. */
  gaps: [] as number[],
  snapLate: 0,
  /** When the last snapshot arrived while visible (0: none since the page was last hidden). */
  lastSnapAt: 0,
  /** How far each server correction moved the player back (m). */
  corrections: [] as number[],
  panels: new Map<string, PanelCount>(),
  open: new Map<string, number>(),
  lastSent: performance.now(),
  sentLoad: false,
};

/** The world is up for the first time this page load (the boot overlay went away). */
export function worldReady(): void {
  if (state.loadMs !== null) return;
  state.loadMs = Math.round(performance.now() - state.started);
  // a short session may never reach the next send or a clean pagehide: report the load early
  setTimeout(send, EARLY_SEND_MS);
}

/** One rendered frame; `dtMs` since the previous one (hidden time excluded by the caller). */
export function frame(now: number, dtMs: number): void {
  state.frames++;
  if (dtMs < 5000) {
    state.visibleMs += dtMs;
    if (dtMs >= LONG_FRAME_MS) state.longFrames++;
  }
  if (now - state.secondStart >= 1000) {
    if (state.secondStart > 0) {
      state.fps.push(state.secondFrames);
      if (state.fps.length > 600) state.fps.shift();
    }
    state.secondStart = now;
    state.secondFrames = 0;
  }
  state.secondFrames++;
  if (now - state.lastSent >= SEND_EVERY_MS) send();
}

export function reconnecting(): void {
  state.reconnects++;
}

/** A ping came back after `ms`. */
export function roundTrip(ms: number): void {
  if (Number.isFinite(ms) && ms >= 0 && state.rtts.length < MAX_RTTS) state.rtts.push(ms);
}

/** A snapshot arrived (only counted while the page is visible: a hidden tab's timers are slow). */
export function snapshotArrived(now: number): void {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    state.lastSnapAt = 0;
    return;
  }
  if (state.lastSnapAt > 0) {
    const gap = now - state.lastSnapAt;
    if (state.gaps.length < MAX_GAPS) state.gaps.push(gap);
    if (gap >= LATE_SNAPSHOT_MS) state.snapLate++;
  }
  state.lastSnapAt = now;
}

/** The snapshot stream restarts (zone travel, a new socket, a welcome): no gap across it. */
export function snapshotStreamReset(): void {
  state.lastSnapAt = 0;
}

/** The server refused a move and put the player back `metres` away. */
export function corrected(metres: number): void {
  if (Number.isFinite(metres) && state.corrections.length < MAX_CORRECTIONS)
    state.corrections.push(metres);
}

/** A message went to the server: if the player did it (not movement and the like), open panels were used. */
export function intentSent(kind: string): void {
  if (!PASSIVE.has(kind)) state.intents++;
}

/** The set of open panels changed (store subscription). */
export function panelsChanged(open: readonly string[]): void {
  for (const name of open) {
    if (state.open.has(name)) continue;
    state.open.set(name, state.intents);
    const c = state.panels.get(name) ?? { opens: 0, empty: 0 };
    if (!state.panels.has(name) && state.panels.size >= MAX_PANELS) continue;
    c.opens++;
    state.panels.set(name, c);
  }
  for (const [name, at] of state.open) {
    if (open.includes(name)) continue;
    state.open.delete(name);
    if (state.intents !== at) continue;
    // opened before the last send: it is counted again here, as one empty use
    const c = state.panels.get(name) ?? { opens: 0, empty: 0 };
    c.empty++;
    if (state.panels.has(name) || state.panels.size < MAX_PANELS) state.panels.set(name, c);
  }
}

const quantile = (xs: number[], q: number): number | null => {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(q * s.length))] ?? null;
};

/** What goes to the server: counts since the last send (load time once). */
function summary(touch: boolean): Record<string, unknown> {
  return {
    device: touch ? "touch" : "desktop",
    ...(state.loadMs !== null && !state.sentLoad ? { loadMs: state.loadMs } : {}),
    // visible play time (hidden time is not frames)
    seconds: Math.round(state.visibleMs / 1000),
    frames: state.frames,
    longFrames: state.longFrames,
    fpsMedian: quantile(state.fps, 0.5),
    fpsP10: quantile(state.fps, 0.1),
    reconnects: state.reconnects,
    rttP50: quantile(state.rtts, 0.5),
    rttP95: quantile(state.rtts, 0.95),
    rttMax: state.rtts.length ? Math.max(...state.rtts) : null,
    snapGapP50: quantile(state.gaps, 0.5),
    snapGapP95: quantile(state.gaps, 0.95),
    snapGapMax: state.gaps.length ? Math.max(...state.gaps) : null,
    snapLate: state.snapLate,
    corrections: state.corrections.length,
    correctionP50M: quantile(state.corrections, 0.5),
    correctionMaxM: state.corrections.length ? Math.max(...state.corrections) : null,
    panels: Object.fromEntries(state.panels),
  };
}

/** Whether this is a touch device, asked when a summary is made (set by the game store). */
let touchProbe: () => boolean = () => false;
export function setTouchProbe(probe: () => boolean): void {
  touchProbe = probe;
}

/**
 * Ship the summary and start counting again. A keepalive fetch (not a beacon, which can't carry
 * the CSRF header the server asks for) still goes out while the page is being hidden or closed.
 */
function send(): void {
  // nothing drawn since the last send (a hidden tab hiding again): nothing to say
  if (state.frames === 0) return;
  try {
    void fetch(`${API_URL}/v1/client-telemetry`, {
      method: "POST",
      headers: { "content-type": "application/json", [HEADER_CSRF]: "1" },
      credentials: "include",
      body: JSON.stringify(summary(touchProbe())),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* the summary never breaks the game */
  }
  if (state.loadMs !== null) state.sentLoad = true;
  state.lastSent = performance.now();
  state.frames = 0;
  state.longFrames = 0;
  state.visibleMs = 0;
  state.fps = [];
  state.reconnects = 0;
  state.rtts = [];
  state.gaps = [];
  state.snapLate = 0;
  state.corrections = [];
  state.panels.clear();
}

/** Send when the page is hidden or closed (the moment a session usually ends). */
export function installSessionSummary(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("pagehide", () => send());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      send();
      state.lastSnapAt = 0;
      // the next fps sample starts fresh when the page is back (not a partial second)
      state.secondStart = 0;
    }
  });
}
